/**
 * PIM VARIANT_ROWS branches: allowed subtype slugs + vitrine navigation.
 * Must stay in sync with scripts/pim_branch_catalog.py
 */

export type PimSibling = {
  group_id: string;
  name: string;
  subtype_tags?: string[];
  min_price?: number;
  variants_count?: number;
  tile_hint?: string | null;
  protocols?: string[];
  subtype_sort_order?: number;
  branch_sort_order?: number;
};

/** Порядок карточек на витрине = CatalogSubtype.sort_order из админки. */
export function sortCatalogSiblings<T extends PimSibling>(siblings: readonly T[]): T[] {
  return [...siblings].sort((a, b) => {
    const ao = a.subtype_sort_order ?? 9999;
    const bo = b.subtype_sort_order ?? 9999;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '', 'ru');
  });
}

export type PimNavTile = {
  subtypeSlug: string;
  label: string;
  hint: string;
};

export type PimBranchUi = {
  branchSlugs: readonly string[];
  subtypeSlugs: ReadonlySet<string>;
  navTiles: readonly PimNavTile[];
  /** Single subtype — hide tabs, redirect stale cards */
  singleSubtype?: string;
  sectionTitle: string;
};

/** Legacy vitrine slugs → PIM subtype (sync with backend/app/pim_branch_registry.py). */
export const PIM_LEGACY_SUBTYPE_REDIRECT: Record<string, Record<string, string>> = {
  lift: {
    standartnaya: 'lift-sistema',
    standart: 'lift-sistema',
    's-elektroprivodom': 'lift-sistema',
  },
  rimskie: {
    klassicheskie: 'rimskiy-elektrokarniz-38mm',
    классические: 'rimskiy-elektrokarniz-38mm',
    's-elektroprivodom': 'rimskiy-elektrokarniz-50mm',
    'с-электроприводом': 'rimskiy-elektrokarniz-50mm',
  },
  plisse: {
    obychnoe: 's-napravlyayushimi',
    obychloe: 's-napravlyayushimi',
    's-elektroprivodom': 's-napravlyayushimi',
    'den-noch': 'tkan',
  },
  yaponskie: {
    standartnye: '3-ryada',
    standart: '3-ryada',
    's-elektroprivodom': '3-ryada',
  },
  zhalyuzi: {
    gorizontalnye: 'gorizontalnye-50mm',
    plisse: '',
  },
};

export const PIM_BRANCH_UI: Record<string, PimBranchUi> = {
  zhalyuzi: {
    branchSlugs: ['zhalyuzi', 'zhalyuzi-shtory', 'elektrokarnizy-dlya-zhalyuzi'],
    subtypeSlugs: new Set([
      'vertikalnye',
      'gorizontalnye-50mm',
      'naklonnye',
      'lameli-gorizontalnye',
      'lameli-vertikalnye',
      'ruchnye-gorizontalnye',
    ]),
    sectionTitle: 'Выберите тип жалюзи',
    navTiles: [
      { subtypeSlug: 'vertikalnye', label: 'Вертикальные', hint: 'Классические вертикальные — MR-2256, управление и размер проёма.' },
      { subtypeSlug: 'gorizontalnye-50mm', label: 'Горизонтальные 50 мм', hint: 'Электропривод MR-350: сеть и АКБ, ZigBee / Wi‑Fi.' },
      { subtypeSlug: 'naklonnye', label: 'Наклонные', hint: 'Наклонно-сдвижные системы под проём.' },
      { subtypeSlug: 'ruchnye-gorizontalnye', label: 'Ручные', hint: 'Механические горизонтальные без электропривода.' },
      { subtypeSlug: 'lameli-gorizontalnye', label: 'Ламели гориз.', hint: 'Цена за м² — подбор цвета ламели.' },
      { subtypeSlug: 'lameli-vertikalnye', label: 'Ламели верт.', hint: 'Алюминиевые ламели 89 мм — цена за м².' },
    ],
  },
  plisse: {
    branchSlugs: ['plisse', 'elektrkaronizy-dlya-plisse', 'elektrokarnizy-dlya-plisse'],
    subtypeSlugs: new Set(['s-napravlyayushimi', 'tkan']),
    sectionTitle: 'Выберите тип плиссе',
    navTiles: [
      { subtypeSlug: 's-napravlyayushimi', label: 'С направляющими', hint: 'Электрокарниз MR-2408: 220В, АКБ, ZigBee, Wi‑Fi.' },
      { subtypeSlug: 'tkan', label: 'Ткань', hint: 'Полотно блэкаут / полупрозрачное — расчёт за м².' },
    ],
  },
  yaponskie: {
    branchSlugs: ['yaponskie', 'yaponskie-shtory', 'yaponskie-elektrokarnizy'],
    subtypeSlugs: new Set(['3-ryada']),
    singleSubtype: '3-ryada',
    sectionTitle: 'Японские шторы',
    navTiles: [
      { subtypeSlug: '3-ryada', label: '3 ряда', hint: 'Классическая японская система на три ряда полотен.' },
    ],
  },
  lift: {
    branchSlugs: ['lift-sistema', 'lift', 'lift-sistemy'],
    subtypeSlugs: new Set(['lift-sistema']),
    singleSubtype: 'lift-sistema',
    sectionTitle: 'Лифт-система',
    navTiles: [
      { subtypeSlug: 'lift-sistema', label: 'Лифт-система', hint: 'MR-450: сеть, RS-485, фазное управление.' },
    ],
  },
  rimskie: {
    branchSlugs: ['rimskie', 'rimskie-shtory', 'rimskie-elektrokarnizy', 'rimskie elektrokarnizy'],
    subtypeSlugs: new Set([
      'rimskiy-elektrokarniz-38mm',
      'rimskiy-elektrokarniz-50mm',
      'rimskiy-elektrokarniz-den-noch-38mm',
      'rimskiy-elektrokarniz-den-noch-50mm',
      'rimskiy-elektrokarniz-new-korob-38mm',
      'rimskiy-elektrokarniz-new-korob-den-noch-38mm',
      'rimskiy-elektrokarniz-v2-0',
    ]),
    sectionTitle: 'Тип римского карниза',
    navTiles: [
      { subtypeSlug: 'rimskiy-elektrokarniz-38mm', label: '38 мм', hint: 'Классический профиль — пульт, RS-485, умный дом.' },
      { subtypeSlug: 'rimskiy-elektrokarniz-50mm', label: '50 мм', hint: 'Усиленный профиль до 3 м, фаза и АКБ.' },
      { subtypeSlug: 'rimskiy-elektrokarniz-den-noch-38mm', label: 'День-ночь 38 мм', hint: 'Двойное полотно, все типы управления.' },
      { subtypeSlug: 'rimskiy-elektrokarniz-den-noch-50mm', label: 'День-ночь 50 мм', hint: 'День-ночь на усиленном профиле.' },
      { subtypeSlug: 'rimskiy-elektrokarniz-new-korob-38mm', label: 'NEW короб 38 мм', hint: 'Скрытый короб, ширина до 2,6 м.' },
      {
        subtypeSlug: 'rimskiy-elektrokarniz-new-korob-den-noch-38mm',
        label: 'NEW короб д/н',
        hint: 'Короб + полотно день-ночь.',
      },
      { subtypeSlug: 'rimskiy-elektrokarniz-v2-0', label: 'V2.0', hint: 'Обновлённая система V2.0.' },
    ],
  },
};

export function pimBranchKeyFromSlug(branchSlug: string | undefined | null): string | null {
  const b = (branchSlug || '').trim().toLowerCase();
  if (!b) return null;
  for (const [key, cfg] of Object.entries(PIM_BRANCH_UI)) {
    if (cfg.branchSlugs.some((s) => s === b || b.includes(s) || s.includes(b))) return key;
  }
  if (b.includes('zhalyuz') || b.includes('жалюз')) return 'zhalyuzi';
  if (b.includes('plisse') || b.includes('плиссе')) return 'plisse';
  if (b.includes('yaponsk') || b.includes('японск')) return 'yaponskie';
  if (b.includes('lift') || b.includes('лифт')) return 'lift';
  if (b.includes('rimsk') || b.includes('римск')) return 'rimskie';
  return null;
}

export function subtypeSlugFromGroupId(groupId: string): string {
  if (!groupId.includes('::')) return '';
  return groupId.split('::', 2)[1]?.trim().toLowerCase() ?? '';
}

export function resolvePimSubtypeSlug(
  branchKey: string,
  subtypeSlug: string,
  cfg: PimBranchUi,
): string {
  const slug = subtypeSlug.trim().toLowerCase();
  if (cfg.subtypeSlugs.has(slug)) return slug;
  const mapped = PIM_LEGACY_SUBTYPE_REDIRECT[branchKey]?.[slug];
  if (mapped === '') return '';
  if (mapped && cfg.subtypeSlugs.has(mapped)) return mapped;
  return cfg.subtypeSlugs.has(slug) ? slug : '';
}

/** Canonical branch slug for group_id (first alias from registry). */
export function pimCanonicalBranchSlug(branchKey: string): string {
  return PIM_BRANCH_UI[branchKey]?.branchSlugs[0] ?? branchKey;
}

/** Rewrite branch::legacy → canonical_branch::pim_subtype before API fetch. */
export function resolvePimGroupId(groupId: string | null | undefined): string | null {
  const gid = (groupId || '').trim();
  if (!gid.includes('::')) return gid || null;
  const [branch, sub] = gid.split('::', 2).map((x) => x.trim().toLowerCase());
  const key = pimBranchKeyFromSlug(branch);
  if (!key) return gid;
  const cfg = PIM_BRANCH_UI[key];
  const resolved = resolvePimSubtypeSlug(key, sub, cfg);
  if (!resolved) return gid;
  const canonBranch = pimCanonicalBranchSlug(key);
  if (resolved === sub && canonBranch === branch) return gid;
  return `${canonBranch}::${resolved}`;
}

/** Default product-group id when vitrine list is empty (import lag / cache). */
export function defaultPimGroupId(branchKey: string): string | null {
  const cfg = PIM_BRANCH_UI[branchKey];
  if (!cfg) return null;
  const branch = pimCanonicalBranchSlug(branchKey);
  const sub = cfg.singleSubtype || cfg.navTiles[0]?.subtypeSlug;
  return sub ? `${branch}::${sub}` : null;
}

export function filterPimCatalogGroups<T extends PimSibling>(
  branchSlug: string | undefined,
  items: T[],
): T[] {
  const key = pimBranchKeyFromSlug(branchSlug || items[0]?.group_id?.split('::')[0]);
  if (!key) return items;
  const cfg = PIM_BRANCH_UI[key];
  const isPimRow = (item: T) => {
    const gid = item.group_id || '';
    if (!gid.includes('::')) return false;
    return cfg.subtypeSlugs.has(subtypeSlugFromGroupId(gid));
  };
  const withVariants = items.filter(
    (item) => isPimRow(item) && Number(item.variants_count ?? 0) > 0,
  );
  if (withVariants.length > 0) return withVariants;
  const pimOnly = items.filter(isPimRow);
  if (pimOnly.length > 0) return pimOnly;
  return items;
}

export function groupIdForSubtype(branchSlug: string, subtypeSlug: string): string {
  return `${branchSlug}::${subtypeSlug}`;
}

/** PIM-only siblings; prefer rows with variants, else any allowed subtype slug. */
export function filterPimSiblings(_branchSlug: string, siblings: PimSibling[], cfg: PimBranchUi): PimSibling[] {
  const isPim = (sg: PimSibling) => {
    const gid = sg.group_id || '';
    if (!gid.includes('::')) return false;
    const [b, sub] = gid.split('::', 2).map((x) => x.trim().toLowerCase());
    if (!cfg.branchSlugs.includes(b)) return false;
    return cfg.subtypeSlugs.has(sub);
  };
  const withVariants = siblings.filter((sg) => isPim(sg) && Number(sg.variants_count ?? 0) > 0);
  if (withVariants.length > 0) return withVariants;
  return siblings.filter(isPim);
}

export function findSiblingForSubtype(
  siblings: PimSibling[],
  branchSlug: string,
  subtypeSlug: string,
): PimSibling | undefined {
  const want = groupIdForSubtype(branchSlug, subtypeSlug).toLowerCase();
  return siblings.find((s) => (s.group_id || '').toLowerCase() === want);
}

export function bestPimSibling(siblings: PimSibling[], _cfg: PimBranchUi): PimSibling | undefined {
  const ranked = sortCatalogSiblings(siblings);
  const withVariants = ranked.filter((s) => Number(s.variants_count ?? 0) > 0);
  return (withVariants.length > 0 ? withVariants : ranked)[0];
}

/** Only tiles that exist in grouped API (respects subtype is_active in admin). */
export function visiblePimNavTiles(
  tiles: readonly PimNavTile[],
  siblings: readonly PimSibling[],
): PimNavTile[] {
  const live = new Set(
    siblings
      .filter((s) => Number(s.variants_count ?? 0) > 0)
      .map((s) => subtypeSlugFromGroupId(s.group_id)),
  );
  const filtered =
    live.size === 0
      ? siblings.length
        ? tiles.filter((t) => siblings.some((s) => subtypeSlugFromGroupId(s.group_id) === t.subtypeSlug))
        : [...tiles]
      : tiles.filter((t) => live.has(t.subtypeSlug));
  return pimNavTilesInCatalogOrder(filtered, siblings);
}

/** Плитки PIM в порядке sort_order подтипов (админка), не захардкоженном navTiles. */
export function pimNavTilesInCatalogOrder(
  tiles: readonly PimNavTile[],
  siblings: readonly PimSibling[],
): PimNavTile[] {
  const bySlug = new Map(tiles.map((t) => [t.subtypeSlug, t]));
  const ordered: PimNavTile[] = [];
  for (const sg of sortCatalogSiblings(siblings)) {
    const slug = subtypeSlugFromGroupId(sg.group_id);
    const tile = bySlug.get(slug);
    if (tile) ordered.push(tile);
  }
  for (const t of tiles) {
    if (!ordered.some((x) => x.subtypeSlug === t.subtypeSlug)) ordered.push(t);
  }
  return ordered;
}
